import { AppPage } from '../types';
import { persistence } from '../services/persistence';
import { getPathForPage } from './navigation';

export interface PageVisit {
  id: string;
  page: AppPage;
  path: string;
  pageName: string;
  subpage?: string; // For subpages like tabs or selected programs
  timestamp: number;
  duration: number; // in milliseconds
  sessionId: string;
}

export interface PageHistory {
  visits: PageVisit[];
  totalTimeSpent: number; // in milliseconds
  lastUpdated: number;
}

const STORAGE_KEY = 'eir_page_history';
const MAX_HISTORY_DAYS = 90; // Keep 90 days of history
const MAX_VISITS = 1000; // Maximum number of visits to store

// Page display names
const PAGE_NAMES: Record<AppPage, string> = {
  'ehr': 'Health Record',
  'journal': 'Journal',
  'eir-chat': 'EIR Chat',
  'calendar': 'Calendar',
  'health-data': 'Health Data',
  'health-assessment': 'Health Assessment',
  'health-programs': 'Health Programs',
  'health-literacy': 'Health Literacy',
  'cbt-programs': 'CBT Programs',
  'health-map': 'Health Map',
  'health-videos': 'Health Videos',
  'health-actions': 'Health Actions',
  'care-coach': 'Care Coach',
  'nutrition': 'Nutrition',
  'request-care': 'Request Care',
  'feedback': 'Feedback',
  'personal-growth': 'Personal Growth',
  'get-care': 'Get Care',
  'tutorial': 'Tutorial',
  'eir-docs': 'EIR Docs',
  'documentation': 'Documentation',
  'roadmap': 'Roadmap',
  'for-you': 'For You',
  'privacy': 'Privacy',
  'skills': 'Skills Library',
  'health-records': 'Health Records',
  'my-health': 'My Health',
  'my-health-mental': 'My Mental Health',
  'my-health-physical': 'My Physical Health',
  'my-health-emotional': 'My Emotional Health',
  'my-health-social': 'My Social Health',
  'tracking': 'Daily Tracking'
};

// Subpage display names for Personal Growth tabs
const PERSONAL_GROWTH_SUBPAGE_NAMES: Record<string, string> = {
  'feedback': 'Feedback',
  'health-actions': 'Health Actions',
  'listen': 'Listen',
  'health-literacy': 'Health Literacy',
  'brain-training': 'Brain Training',
  'intelligence': 'Intelligence Training',
  'puzzles': 'Health Puzzles',
  'calendar': 'Calendar',
  'voice': 'Voice Care',
  'breathe': 'Breathe',
  'writing': 'Writing',
  'room-for-improvement': 'Room for Improvement',
  'habit-tracker': 'Habit Tracker',
  'emotions': 'Emotions',
  'sleep': 'Sleep',
  'food': 'Food',
  'water': 'Water',
  'information': 'Information',
  'relationships': 'Relationships',
  'movement': 'Movement',
  'thinking': 'The Art of Thinking',
  'action-plan': 'Action Plans',
};

// Cache for program names (populated when programs are loaded)
const programNameCache = new Map<string, string>();

// Resolve program name from ID
const resolveProgramName = (programId: string): string => {
  // Check cache first
  if (programNameCache.has(programId)) {
    return programNameCache.get(programId)!;
  }

  // Try to load from localStorage (user-created programs)
  const userPrograms = persistence.getJSON<any[]>('user-created-cbt-programs', []);
  if (userPrograms.length > 0) {
    // First try exact match
    let program = userPrograms.find((p: any) => p.id === programId);

    // If not found and it looks like a custom program, try to find by partial match
    // Handle cases where ID format might have changed (e.g., "Custom 1762947373706 4q6msir" vs "cbt-custom-1762947373706-0-4q6msir")
    if (!program && (programId.startsWith('Custom ') || programId.startsWith('cbt-custom-'))) {
      const timestampMatch = programId.match(/\d{13}/);
      if (timestampMatch) {
        const timestamp = timestampMatch[0];
        program = userPrograms.find((p: any) => 
          p.id && (p.id.includes(timestamp) || p.id.includes(programId.replace(/^Custom /, '')))
        );
      }
    }

    if (program && program.title) {
      programNameCache.set(programId, program.title);
      return program.title;
    }
  }

  // Fallback: format the ID nicely
  // Handle custom program IDs like "Custom 1762947373706 4q6msir"
  if (programId.startsWith('Custom ') || programId.startsWith('cbt-custom-')) {
    // Try to extract a meaningful name, or use a generic fallback
    return 'Custom Program';
  }

  // Format standard program IDs
  const formatted = programId
    .replace(/^cbt-/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
  
  return formatted;
};

// Function to populate program name cache (can be called when programs are loaded)
export const updateProgramNameCache = (programs: Array<{ id: string; title: string }>): void => {
  programs.forEach(program => {
    if (program.id && program.title) {
      programNameCache.set(program.id, program.title);
    }
  });
};

export const getPageName = (page: AppPage, subpage?: string): string => {
  const baseName = PAGE_NAMES[page] || page;
  
  // Add subpage information for specific pages
  if (subpage) {
    if (page === 'personal-growth' && PERSONAL_GROWTH_SUBPAGE_NAMES[subpage]) {
      return `${baseName} - ${PERSONAL_GROWTH_SUBPAGE_NAMES[subpage]}`;
    }
    if (page === 'health-programs' || page === 'cbt-programs') {
      // For programs, resolve the actual program name
      const programName = resolveProgramName(subpage);
      return `${baseName} - ${programName}`;
    }
  }
  
  return baseName;
};

// Get current session ID (persists for the browser session)
const getSessionId = (): string => {
  if (typeof window === 'undefined') return '';
  if (!sessionStorage.getItem('eir_session_id')) {
    sessionStorage.setItem('eir_session_id', `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);
  }
  return sessionStorage.getItem('eir_session_id') || '';
};

// Load history from localStorage
export const loadHistory = (): PageHistory => {
  const defaultHistory: PageHistory = { visits: [], totalTimeSpent: 0, lastUpdated: Date.now() };
  const history = persistence.getJSON<PageHistory | null>(STORAGE_KEY, null);
  if (!history) return defaultHistory;

  // Clean up old visits (older than MAX_HISTORY_DAYS)
  const cutoffTime = Date.now() - (MAX_HISTORY_DAYS * 24 * 60 * 60 * 1000);
  const filteredVisits = history.visits.filter(visit => visit.timestamp > cutoffTime);

  // Limit to MAX_VISITS (keep most recent)
  const limitedVisits = filteredVisits
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_VISITS);

  // Recalculate total time
  const totalTimeSpent = limitedVisits.reduce((sum, visit) => sum + visit.duration, 0);

  return {
    visits: limitedVisits,
    totalTimeSpent,
    lastUpdated: Date.now()
  };
};

// Save history to localStorage
const saveHistory = (history: PageHistory): void => {
  persistence.setJSON(STORAGE_KEY, history);
};

// Add a page visit
export const addPageVisit = (page: AppPage, duration: number, subpage?: string): void => {
  if (typeof window === 'undefined') return;
  if (duration < 1000) return; // Don't track visits shorter than 1 second

  const history = loadHistory();
  const path = getPathForPage(page);
  const paramName = page === 'personal-growth' || page === 'journal' || page === 'ehr' ? 'tab' : 'program';
  const fullPath = subpage ? `${path}${path.includes('?') ? '&' : '?'}${paramName}=${subpage}` : path;
  
  const visit: PageVisit = {
    id: `visit-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    page,
    path: fullPath,
    pageName: getPageName(page, subpage),
    subpage,
    timestamp: Date.now() - duration, // Start time
    duration,
    sessionId: getSessionId()
  };

  history.visits.push(visit);
  history.totalTimeSpent += duration;
  history.lastUpdated = Date.now();

  // Clean up old visits
  const cutoffTime = Date.now() - (MAX_HISTORY_DAYS * 24 * 60 * 60 * 1000);
  history.visits = history.visits
    .filter(v => v.timestamp > cutoffTime)
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_VISITS);

  // Recalculate total time
  history.totalTimeSpent = history.visits.reduce((sum, visit) => sum + visit.duration, 0);

  saveHistory(history);
};

// Get aggregated stats by page
export interface PageStats {
  page: AppPage;
  pageName: string;
  subpage?: string;
  visitCount: number;
  totalTime: number; // in milliseconds
  lastVisit: number;
  averageTime: number; // in milliseconds
}

export const getPageStats = (): PageStats[] => {
  const history = loadHistory();
  // Group by page + subpage combination to show individual subpages
  const statsMap = new Map<string, { page: AppPage; subpage?: string; visits: PageVisit[] }>();

  // Group visits by page + subpage combination
  history.visits.forEach(visit => {
    const key = visit.subpage ? `${visit.page}::${visit.subpage}` : visit.page;
    if (!statsMap.has(key)) {
      statsMap.set(key, { page: visit.page, subpage: visit.subpage, visits: [] });
    }
    statsMap.get(key)!.visits.push(visit);
  });

  // Calculate stats for each page/subpage combination
  const stats: PageStats[] = Array.from(statsMap.entries()).map(([key, data]) => {
    const visits = data.visits.sort((a, b) => b.timestamp - a.timestamp);
    const totalTime = visits.reduce((sum, visit) => sum + visit.duration, 0);
    const averageTime = visits.length > 0 ? totalTime / visits.length : 0;
    // Refresh page name to get updated program names (in case cache was populated after visit was recorded)
    const pageName = getPageName(data.page, data.subpage);

    return {
      page: data.page,
      pageName,
      subpage: data.subpage,
      visitCount: visits.length,
      totalTime,
      lastVisit: visits[0]?.timestamp || 0,
      averageTime
    };
  });

  // Sort by last visit (most recent first)
  return stats.sort((a, b) => b.lastVisit - a.lastVisit);
};

// Clear all history
export const clearHistory = (): void => {
  persistence.removeItem(STORAGE_KEY);
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('eir_session_id');
  }
};

// Format duration for display
export const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

// Format relative time
export const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  if (weeks < 4) return `${weeks}w ago`;
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

// Get daily activity data for heatmap calendar
export interface DailyActivity {
  date: string; // YYYY-MM-DD format
  count: number; // Number of visits on this day
  totalTime: number; // Total time spent in milliseconds
}

export const getDailyActivity = (days: number = 90): DailyActivity[] => {
  const history = loadHistory();
  const now = Date.now();
  const startDate = now - (days * 24 * 60 * 60 * 1000);
  
  // Initialize all days with zero activity
  const activityMap = new Map<string, { count: number; totalTime: number }>();
  
  // Get all visits within the date range
  const relevantVisits = history.visits.filter(visit => {
    const visitDate = visit.timestamp;
    return visitDate >= startDate && visitDate <= now;
  });
  
  // Group visits by date (using local timezone consistently)
  relevantVisits.forEach(visit => {
    // Use the visit timestamp to get the local date
    const date = new Date(visit.timestamp);
    // Ensure we're using local date components (not UTC)
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();
    const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    if (!activityMap.has(dateKey)) {
      activityMap.set(dateKey, { count: 0, totalTime: 0 });
    }
    
    const dayActivity = activityMap.get(dateKey)!;
    dayActivity.count += 1;
    dayActivity.totalTime += visit.duration;
  });
  
  // Create array for all days in range (using local timezone consistently)
  const result: DailyActivity[] = [];
  const currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0); // Normalize to midnight local time
  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999); // End of today
  
  while (currentDate <= endDate) {
    // Use local date components consistently
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const day = currentDate.getDate();
    const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const activity = activityMap.get(dateKey) || { count: 0, totalTime: 0 };
    
    result.push({
      date: dateKey,
      count: activity.count,
      totalTime: activity.totalTime
    });
    
    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return result;
};

// Calculate current streak (consecutive days with activity)
export const getCurrentStreak = (): number => {
  const dailyActivity = getDailyActivity(365); // Check last year
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Check if today has activity
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const todayActivity = dailyActivity.find(d => d.date === todayKey);
  
  // If no activity today, streak is 0
  if (!todayActivity || todayActivity.count === 0) {
    return 0;
  }
  
  // Start counting from today (included) and go backwards
  let streak = 1; // Today has activity
  let checkDate = new Date(today);
  checkDate.setDate(checkDate.getDate() - 1); // Start checking from yesterday
  
  // Count backwards
  for (let i = 0; i < 365; i++) {
    const dateKey = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
    const dayActivity = dailyActivity.find(d => d.date === dateKey);
    
    if (dayActivity && dayActivity.count > 0) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }
  
  return streak;
};

